import { RecognitionBadge } from "@/components/recognition-badge/RecognitionBadge.tsx";
import { ApiEmployeeRecognitionTemplate } from "@/types/recognitions/recognition.types.ts";
import {
  Box,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { CSSProperties, MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

const SPRING_DAMPING = 6;
const SPRING_FREQUENCY = 12;
const SCATTER_DURATION_MS = 900;
const NUDGE_DURATION_MS = 280;
const NUDGE_FACTOR = 0.5;
const BLUR_MIN = 0.25;
const BLUR_MAX = 1;

interface EmployeeGiveRecognitionButtonProps {
  badges?: ApiEmployeeRecognitionTemplate[];
  count?: number;
  onGive?: () => void;
  viewHref?: string;
  className?: string;
  isLoading?: boolean;
}

interface ScatterTarget {
  x: number;
  y: number;
  rotate: number;
}

const buildSpringEasing = (damping: number, frequency: number, steps = 60): string => {
  const points = Array.from({ length: steps + 1 }, (_, i) => {
    const t = i / steps;
    const value = 1 - Math.exp(-damping * t) * Math.cos(frequency * t);
    return Number(value.toFixed(4));
  });
  points[points.length - 1] = 1;
  return `linear(${points.join(", ")})`;
};

const springEasing = buildSpringEasing(SPRING_DAMPING, SPRING_FREQUENCY);

const scatterPositions: ScatterTarget[] = [
  { x: 0, y: 0, rotate: 0 },
  { x: -30, y: -20, rotate: -15 },
  { x: 30, y: -26, rotate: 15 },
  { x: 0, y: -44, rotate: -10 },
  { x: 44, y: -40, rotate: 20 },
  { x: -44, y: -46, rotate: -20 },
  { x: 6, y: -66, rotate: 15 },
];

const restingTransform = (index: number): string => {
  if (index === 1) return "translate(-2px, -2px)";
  if (index === 2) return "translate(-4px, -4px)";
  return "translate(0px, 0px)";
};

const scatterTransform = ({ x, y, rotate }: ScatterTarget, factor: number): string =>
  `translate(${x * factor}px, ${y * factor}px) rotate(${rotate * factor}deg)`;

const cardBlur = (index: number, total: number): number => {
  if (index < 4) return 0;
  const last = total - 1;
  if (last <= 4) return BLUR_MIN;
  return BLUR_MIN + ((index - 4) / (last - 4)) * (BLUR_MAX - BLUR_MIN);
};

export function EmployeeGiveRecognitionButton({
  badges = [],
  count = 0,
  onGive,
  viewHref,
  className,
  isLoading = false,
}: EmployeeGiveRecognitionButtonProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const stopPropagation = (e: MouseEvent<HTMLDivElement>) => e.stopPropagation();

  const handleGive = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onGive?.();
  };

  if (isLoading) {
    return (
      <Box
        className={twMerge(
          "flex h-9 w-9 items-center justify-center rounded-xs border border-dark/10 bg-light text-lg text-secondary",
          className
        )}
      >
        <FontAwesomeIcon
          icon={"fa-light fa-spinner"}
          className={"animate-spin [animation-duration:6.5s]"}
        />
      </Box>
    );
  }

  if (!badges.length) {
    if (!onGive) return null;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Box
            onClick={handleGive}
            className={twMerge(
              "flex h-9 w-9 cursor-pointer items-center justify-center rounded-xs border border-dark/10 bg-light text-lg text-secondary transition-colors",
              className
            )}
          >
            <FontAwesomeIcon icon={"fa-light fa-hand-wave"} />
          </Box>
        </TooltipTrigger>
        <TooltipContent>{t("recognition:give_first_recognition")}</TooltipContent>
      </Tooltip>
    );
  }

  const cardStyle = (index: number): CSSProperties => {
    const target = scatterPositions[index];
    const blur = cardBlur(index, badges.length);
    const filter = blur > 0 ? `blur(${blur}px)` : undefined;
    const spring = `transform ${SCATTER_DURATION_MS}ms ${springEasing} ${index * 14}ms`;

    if (isOpen) return { transform: scatterTransform(target, 1), transition: spring, filter };

    if (isHovered) {
      return {
        transform: scatterTransform(target, NUDGE_FACTOR),
        transition: `transform ${NUDGE_DURATION_MS}ms ease-out ${index * 6}ms`,
        filter,
      };
    }

    return { transform: restingTransform(index), transition: spring, filter };
  };

  const order = badges.map((_, index) => index).reverse();

  return (
    <Box onClick={stopPropagation} className={className}>
      <DropdownMenu
        placement={"bottom-start"}
        open={isOpen}
        onOpenChange={setIsOpen}
        trigger={
          <Box
            className={"relative h-9 w-9 cursor-pointer"}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {order.map((index) => (
              <Box key={index} className={"absolute inset-0"} style={cardStyle(index)}>
                <RecognitionBadge template={badges[index]} size={"xs"} />
              </Box>
            ))}
            {count > 0 && (
              <Box
                className={
                  "absolute -top-2 -right-1.75 z-10 flex h-4 min-w-4 items-center justify-center rounded-full bg-warning px-1 text-xs font-semibold leading-4 text-light"
                }
              >
                {count}
              </Box>
            )}
          </Box>
        }
      >
        {onGive && (
          <DropdownMenuItem
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-hand-wave"} />}
            title={t("recognition:give_recognition")}
            onClick={onGive}
          />
        )}
        <DropdownMenuItem
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-cards"} />}
          title={t("recognition:view_recognitions", { count })}
          to={viewHref}
        />
      </DropdownMenu>
    </Box>
  );
}
